import React, { useEffect, useCallback } from "react";
import { Node } from "reactflow";
import { Input } from "@components/ui/input";
import { useTitleValidation } from "../hooks/useTitleValidation";

interface ValidatedTitleInputProps {
  id: string;
  title: string;
  onChange: (value: string) => void;
  nodes: Node[];
  dataTitle?: string; // Optional: title from data prop (for syncing from backend)
  onValidationChange?: (isValid: boolean) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Reusable title input component with built-in duplicate validation
 * Displays red border and error message only when exact complete title match is detected
 * Automatically syncs from dataTitle prop when provided (no need for useEffect in parent)
 */
export const ValidatedTitleInput: React.FC<ValidatedTitleInputProps> = ({
  id,
  title,
  onChange,
  nodes,
  dataTitle,
  onValidationChange,
  placeholder = "Enter node title",
  className = "nodrag",
  disabled = false,
}) => {
  const { titleError, isValid } = useTitleValidation(id, title, nodes);
  const hasSyncedRef = React.useRef(false);
  // Sync from parent data.title when it changes (handles backend loading)
  useEffect(() => {
    if (!hasSyncedRef.current && dataTitle) {
      hasSyncedRef.current = true;
      onChange(dataTitle);
    }
  }, [dataTitle]);

  // Notify parent component when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // Memoize the change handler
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Memoize the keydown handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div className="space-y-1 w-full">
      <Input
        value={title}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={`text-xs ${titleError ? "border-red-500 focus-visible:ring-red-500" : ""} ${className}`}
      />
      {titleError && <p className="text-xs text-red-500 mt-1">{titleError}</p>}
    </div>
  );
};
